import { useEffect } from 'react';
import CheckInProgressChart from './CheckInProgressChart';
import ProgressChart from './ProgressChart';
import { formatKeystrokeCount } from '../lib/activityLog';
import {
  dictationRetentionGap,
  performanceByPresentation,
  topSlowKeys,
  topWeakKeys,
  type PresentationKind,
} from '../lib/stats';
import type { UserProfile } from '../types';

interface ProgressModalProps {
  user: UserProfile;
  onClose: () => void;
}

export default function ProgressModal({ user, onClose }: ProgressModalProps) {
  const weak = topWeakKeys(user.errorsByKey, 5);
  const slow = topSlowKeys(user.responseTimeAvgs, 3, 1);
  const presentationStats = performanceByPresentation(user.recentResults);
  const retentionGap = dictationRetentionGap(user.recentResults);
  const hasNonStandard = (
    ['dictation', 'echo', 'metronome', 'shadow'] as PresentationKind[]
  ).some((k) => presentationStats[k].n > 0);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-12 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="progress-modal-title"
      onClick={onClose}
    >
      <div
        className="mb-8 w-full max-w-3xl animate-fade-in-up rounded-2xl bg-white p-5 shadow-xl ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="progress-modal-title" className="text-xl font-semibold text-strong">
              Din udvikling
            </h2>
            <p className="mt-1 text-xs text-faint">
              Tastetryk og øvelser over tid, plus hastighedstests og trin-WPM.
            </p>
            {(user.lifetimeStats?.totalKeystrokes ?? 0) > 0 && (
              <p className="mt-2 text-sm text-muted">
                I alt har du skrevet{' '}
                <span className="font-mono font-medium text-strong">
                  {formatKeystrokeCount(user.lifetimeStats!.totalKeystrokes)}
                </span>{' '}
                tastetryk
                {user.lifetimeStats!.sessionCount > 0 && (
                  <>
                    {' '}
                    i {user.lifetimeStats!.sessionCount}{' '}
                    {user.lifetimeStats!.sessionCount === 1
                      ? 'øvelse'
                      : 'øvelser'}
                  </>
                )}
                .
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary shrink-0"
            aria-label="Luk"
          >
            Luk
          </button>
        </div>

        {(user.baselineTranscription || user.baselineComposition) && (
          <div className="mt-4 flex flex-wrap gap-4 rounded-xl bg-zinc-50 p-3 text-sm dark:bg-zinc-800/50">
            {user.baselineTranscription && (
              <span className="text-muted">
                Udgangspunkt afskrift:{' '}
                <span className="font-mono font-medium text-strong">
                  {user.baselineTranscription.wpm.toFixed(0)} wpm ·{' '}
                  {(user.baselineTranscription.accuracy * 100).toFixed(0)}%
                </span>
              </span>
            )}
            {user.baselineComposition && (
              <span className="text-muted">
                Udgangspunkt hverdag:{' '}
                <span className="font-mono font-medium text-strong">
                  {user.baselineComposition.wpm.toFixed(0)} wpm
                </span>
              </span>
            )}
          </div>
        )}

        <div className="mt-4 space-y-6">
          <section>
            <h3 className="label">Hastighed over tid</h3>
            <p className="mt-1 text-xs text-faint">
              Bedste WPM pr. dag fra hastighedstests — solidt repertoire.
            </p>
            <div className="mt-3">
              <CheckInProgressChart checkIns={user.wpmCheckIns} />
            </div>
          </section>
          <section>
            <h3 className="label">Træning (trin-lektioner)</h3>
            <p className="mt-1 text-xs text-faint">
              Daglig WPM fra pensum — kan svinge når du lærer nye taster.
            </p>
            <div className="mt-3">
              <ProgressChart dailySummaries={user.dailySummaries} />
            </div>
          </section>
        </div>

        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <div>
            <h3 className="label">Svage taster</h3>
            {weak.length === 0 ? (
              <p className="mt-2 text-sm text-muted">Ikke nok data endnu.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {weak.map((w) => (
                  <KeyChip key={w.key} char={w.key} sub={`${Math.round(w.count)} fejl`} />
                ))}
              </div>
            )}
          </div>
          <div>
            <h3 className="label">Langsomste taster</h3>
            {slow.length === 0 ? (
              <p className="mt-2 text-sm text-muted">Ikke nok data endnu.</p>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                {slow.map((s) => (
                  <KeyChip
                    key={s.key}
                    char={s.key}
                    sub={`${Math.round(s.avgMs)} ms`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {hasNonStandard && (
          <div className="mt-6">
            <h3 className="label">Sådan klarer du dig</h3>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {(
                [
                  ['standard', 'Standard'],
                  ['dictation', 'Diktat'],
                  ['echo', 'Flash'],
                  ['metronome', 'Rytme'],
                  ['shadow', 'Skygge'],
                ] as Array<[PresentationKind, string]>
              )
                .filter(([k]) => presentationStats[k].n > 0)
                .map(([k, label]) => {
                  const s = presentationStats[k];
                  return (
                    <div
                      key={k}
                      className="rounded-xl bg-zinc-50 p-3 ring-1 ring-zinc-200 dark:bg-zinc-900/40 dark:ring-white/5"
                    >
                      <p className="text-xs font-medium text-strong">{label}</p>
                      <p className="mt-1 font-mono text-sm">
                        {(s.accuracy * 100).toFixed(0)}% · {s.wpm.toFixed(0)} wpm
                      </p>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {retentionGap && (
          <p className="mt-4 text-xs text-muted">
            Diktat vs. visuel: gap {(retentionGap.gap * 100).toFixed(1)} procentpoint
          </p>
        )}
      </div>
    </div>
  );
}

function KeyChip({ char, sub }: { char: string; sub: string }) {
  const display = char === ' ' ? 'mellemrum' : char;
  return (
    <span className="inline-flex items-center gap-1 rounded-lg bg-zinc-100 px-2 py-1 font-mono text-sm dark:bg-zinc-800">
      <span className="font-semibold text-strong">{display}</span>
      <span className="text-xs text-faint">{sub}</span>
    </span>
  );
}
